import React, { useEffect } from "react";
import { useNicepay } from "../hooks";
import type { NicepayPaymentRequest } from "../lib";
// ...existing code...
import { Header } from "@/widgets/header";
import { Footer } from "@/widgets/footer";
import { request } from "@/shared/api/client";

export interface CheckoutDetailProps {
  planId: string;
  planName: string;
  price: number;
  vat: number;
  total: number;
  userEmail: string;
  planCategory: string; // 상품 카테고리 (개인, 단체 등)
}

/**
 * 결제 상세페이지 컴포넌트 (FSD 아키텍처 기반)
 * - props로 결제 정보 전달
 * - 카드 정보는 PG사 결제 모듈에서 처리
 * - 추후 API 연동 구조 반영
 */
export function CheckoutDetail({
  planId,
  planName,
  price,
  vat,
  total,
  userEmail,
  planCategory,
}: CheckoutDetailProps) {
  const { pay, isLoading, error } = useNicepay();

  // 카테고리가 개인이 아니면 정기결제
  const isRecurringPayment = planCategory !== "개인";

  // NICEPAY 스크립트 로드 및 콜백 등록
  useEffect(() => {
    // 결제 완료 콜백 (나이스페이 SDK가 호출)
    (window as any).nicepaySubmit = () => {
      console.log("✅ nicepaySubmit - submitting form");
      document.forms["payForm" as any].submit();
    };

    // 결제창 닫기 콜백
    (window as any).nicepayClose = () => {
      console.log("⚠️ nicepayClose called - 결제 취소");
      alert("결제가 취소되었습니다.");
    };

    // 이미 로드된 경우 스킵
    if ((window as any).goPay) {
      console.log("✅ NICEPAY goPay already loaded");
      return;
    }

    const script = document.createElement("script");
    script.src = "https://pg-web.nicepay.co.kr/v3/common/js/nicepay-pgweb.js";
    script.type = "text/javascript";
    script.async = true;

    script.onload = () => {
      console.log("✅ NICEPAY script loaded successfully");
      if ((window as any).goPay) {
        console.log("✅ goPay function is available");
      } else {
        console.warn("⚠️ goPay function not found after script load");
      }
    };

    script.onerror = () => {
      console.error("❌ Failed to load NICEPAY script");
    };

    document.head.appendChild(script);

    return () => {
      // 콜백 함수 정리
      delete (window as any).nicepaySubmit;
      delete (window as any).nicepayClose;
    };
  }, []);

  const handlePayment = async () => {
    try {
      // 🧪 테스트용 더미 데이터 (백엔드 연결 안 될 때 사용)
      const USE_DUMMY_DATA = false;

      let backendData: any;

      if (USE_DUMMY_DATA) {
        console.log("🧪 Using TEST DUMMY DATA for recurring payment");
        backendData = {
          payment_type: "RECURRING",
          MID: "517813292m", // 정기결제용 MID
          GoodsName: "베이직",
          Amt: "1004",
          Moid: `moid${new Date().getTime()}`,
          BuyerName: "테스트사용자",
          BuyerEmail: userEmail,
          BuyerTel: "01012345678",
          EdiDate: "20260102144734",
          SignData:
            "8447c1d3458d705f0cc6071c359e6633f567b1417a9a12049f41d38e48f89248", // 테스트용 임시값
        };
      } else {
        // 백엔드에 결제 정보 요청
        const backendResponse = await request<any>({
          method: "POST",
          url: "/v1/payment/info",
          data: {
            planId: planId,
            buyerEmail: userEmail,
            isRecurringPayment, // 정기결제 여부 전달
          },
        });

        // 응답은 두 가지 형식이 혼재: camelCase 또는 UPPERCASE
        backendData = backendResponse?.data || backendResponse;
      }

      // 필수 필드 검증
      const paymentType = backendData?.payment_type;

      if (!paymentType) {
        console.error("❌ Missing payment_type in backend response");
        console.error("Backend Response Full:", backendData);
        alert("백엔드 응답에 필수 정보가 없습니다. 관리자에게 문의하세요.");
        return;
      }

      // 백엔드의 payment_type으로 정기결제 여부 판단 (프론트의 isRecurringPayment 무시)
      const isRecurring = paymentType === "RECURRING";
      console.log("🔍 Payment Type from Backend:", paymentType);
      console.log("🔍 Is Recurring:", isRecurring);

      // 결제 타입 저장 (BillingKeyResult에서 사용)
      sessionStorage.setItem(
        "paymentType",
        isRecurring ? "RECURRING" : "ONE_TIME"
      );
      console.log(
        "📊 Backend Response Data:",
        JSON.stringify(backendData, null, 2)
      );

      // 응답이 camelCase 또는 UPPERCASE인지 판단하여 적절히 접근
      // ONE_TIME: camelCase (goodsName, amt, merchantId, moid 등)
      // RECURRING: UPPERCASE (GoodsName, Amt, MID, Moid 등)
      // ⚠️ 백엔드 데이터를 그대로 사용 - SignData 검증을 위해 변환하지 않음

      const paymentInfo = {
        goodsName: backendData?.goodsName || backendData?.GoodsName,
        amt: backendData?.amt || backendData?.Amt, // 문자열 그대로 유지
        merchantId: backendData?.merchantId || backendData?.mid || "517813292m", // 기본값 설정
        moid: backendData?.moid || backendData?.Moid,
        buyerName: backendData?.buyerName || backendData?.BuyerName,
        buyerEmail: backendData?.buyerEmail || backendData?.BuyerEmail,
        buyerTel: backendData?.buyerTel || backendData?.BuyerTel,
        returnUrl: backendData?.returnUrl || backendData?.ReturnURL,
        signData: backendData?.signData || backendData?.SignData,
        ediDate: backendData?.ediDate || backendData?.EdiDate,
        billingKey: backendData?.billingKey || backendData?.BillingKey,
        paymentType: paymentType,
      };

      console.log(
        "💳 Extracted paymentInfo:",
        JSON.stringify(paymentInfo, null, 2)
      );

      // 필수 필드 재확인
      if (
        !paymentInfo.goodsName ||
        !paymentInfo.amt ||
        !paymentInfo.merchantId ||
        !paymentInfo.moid
      ) {
        console.error("❌ Missing required fields:", {
          goodsName: paymentInfo.goodsName,
          amt: paymentInfo.amt,
          merchantId: paymentInfo.merchantId,
          moid: paymentInfo.moid,
        });
        console.error(
          "Backend Response:",
          JSON.stringify(backendData, null, 2)
        );
        return;
      }

      const paymentData: NicepayPaymentRequest = {
        goodsName: paymentInfo.goodsName,
        amt: paymentInfo.amt,
        merchantId: paymentInfo.merchantId,
        moid: paymentInfo.moid,
        buyerName: paymentInfo.buyerName || userEmail,
        buyerEmail: paymentInfo.buyerEmail || userEmail,
        buyerTel: paymentInfo.buyerTel,
        returnUrl: paymentInfo.returnUrl,
        signData: paymentInfo.signData,
        ediDate: paymentInfo.ediDate,
        authToken: undefined,
        mode: isRecurring ? "subscribe" : "payment",
        needCardInfo: true,
        ...(isRecurring && {
          isRecurring: true,
          recurringInterval: "M",
          billingKey: paymentInfo.billingKey,
        }),
      };

      console.log(
        "📤 Sending paymentData to NICEPAY:",
        JSON.stringify(paymentData, null, 2)
      );
      await pay(paymentData);
    } catch (err) {
      console.error("❌ Payment Error:", err);
      alert("결제 처리 중 오류가 발생했습니다. 다시 시도해주세요.");
    }
  };
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: "#f9f9f9",
      }}
    >
      {/* Main Content */}
      <main
        style={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            maxWidth: 600,
            margin: "clamp(16px, 5vw, 32px) auto",
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            padding: "clamp(16px, 4vw, 32px)",
            boxSizing: "border-box",
          }}
        >
          {/* 결제 내역 */}
          <h2
            style={{
              fontSize: "clamp(18px, 5vw, 24px)",
              marginBottom: 32,
              fontWeight: 700,
            }}
          >
            {planName} 플랜 구독하기
          </h2>
          {/* 결제 정보 */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "clamp(13px, 3vw, 14px)",
                }}
              >
                <span style={{ color: "#666" }}>플랜 금액</span>
                <span style={{ fontWeight: 500 }}>
                  {price.toLocaleString()}원
                </span>
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "clamp(13px, 3vw, 14px)",
                }}
              >
                <span style={{ color: "#666" }}>부가가치세</span>
                <span style={{ fontWeight: 500 }}>{0}원</span>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontWeight: 700,
                fontSize: "clamp(16px, 4vw, 18px)",
                borderTop: "2px solid #f0f0f0",
                paddingTop: 12,
              }}
            >
              <span>최종 결제 금액</span>
              <span style={{ color: "#ff6b00" }}>
                {price.toLocaleString()}원
              </span>
            </div>
          </div>
          {/* 구매자 정보 */}
          <div
            style={{
              marginBottom: 32,
              paddingBottom: 32,
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <h3
              style={{
                fontSize: "clamp(14px, 3.5vw, 16px)",
                fontWeight: 600,
                marginBottom: 16,
              }}
            >
              구매자 정보
            </h3>
            <div style={{ marginBottom: 16 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 8,
                  fontSize: "clamp(13px, 3vw, 14px)",
                  fontWeight: 500,
                }}
              >
                이메일
              </label>
              <input
                type="email"
                value={userEmail}
                readOnly
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 6,
                  border: "1px solid #ddd",
                  backgroundColor: "#f9f9f9",
                  fontSize: "clamp(13px, 3vw, 14px)",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>
          {/* 약관 동의 */}
          <div style={{ marginBottom: 24 }}>
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                fontSize: "clamp(12px, 3vw, 14px)",
                cursor: "pointer",
                lineHeight: 1.4,
              }}
            >
              <input
                type="checkbox"
                defaultChecked
                style={{
                  width: 18,
                  height: 18,
                  cursor: "pointer",
                  marginTop: 2,
                  flexShrink: 0,
                }}
              />
              <span>서비스 약관 및 개인정보 처리방침에 동의합니다.</span>
            </label>
          </div>

          {/* 결제 버튼 (PG사 결제 모듈로 이동) */}
          <button
            onClick={handlePayment}
            disabled={isLoading}
            style={{
              width: "100%",
              padding: "clamp(12px, 3vw, 14px) 16px",
              background: isLoading ? "#ccc" : "#ff6b00",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              fontWeight: 700,
              fontSize: "clamp(14px, 4vw, 16px)",
              cursor: isLoading ? "not-allowed" : "pointer",
              transition: "background 0.3s",
              boxSizing: "border-box",
            }}
            onMouseEnter={(e) => {
              if (!isLoading)
                (e.target as HTMLButtonElement).style.background = "#e55a00";
            }}
            onMouseLeave={(e) => {
              if (!isLoading)
                (e.target as HTMLButtonElement).style.background = "#ff6b00";
            }}
          >
            {isLoading ? "결제 처리 중..." : "결제하기"}
          </button>

          {/* 에러 메시지 */}
          {error && (
            <div
              style={{
                marginTop: 12,
                padding: "10px 12px",
                backgroundColor: "#fee",
                borderLeft: "4px solid #f44",
                borderRadius: 4,
                fontSize: "clamp(12px, 3vw, 14px)",
                color: "#c33",
              }}
            >
              {error}
            </div>
          )}

          {/* 보안 안내 */}
          <div
            style={{
              marginTop: 24,
              padding: "clamp(10px, 3vw, 12px) 14px",
              backgroundColor: "#f0f9ff",
              borderLeft: "4px solid #0284c7",
              borderRadius: 4,
              fontSize: "clamp(11px, 2.5vw, 12px)",
              color: "#666",
              lineHeight: 1.5,
            }}
          >
            🔒 결제 정보는 안전한 결제 게이트웨이를 통해 암호화되어 처리됩니다.
          </div>
        </div>
      </main>
    </div>
  );
}

export default CheckoutDetail;
